import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, shareReplay} from 'rxjs/operators';

import {HttpService} from 'src/app/services/http.service';
import {SearchOptionsRequest} from 'src/app/shared/models/SearchOptionsRequest.model';
import {SearchOptionsResponse} from 'src/app/shared/models/SearchOptionsResponse.model';
import {FundraisingReqRes} from 'src/app/dashboard/shared/holding/fundraising/fundraisings-tab/FundraisingReqRes.model';
import {FundraisingTableItemResponse} from 'src/app/dashboard/shared/holding/fundraising/fundraisings-tab/FundraisingTableItemResponse.model';
import FundraisingStatus from 'src/app/shared/enums/FundraisingStatus.enum';
import {ContributionDetailsItemReqRes} from '../../dashboard/shared/holding/fundraising/import-fundraisings/contribution-details/ContributionDetailsItemReqRes';
import {ContributionsImportResponse} from '../../shared/models/ContributionsImportResponse.model';
import {OfferingDeckEngagementResponse} from '../../shared/models/OfferingDeckEngagementResponse.model';
import {TransferOwnershipReqRes} from '../../dashboard/shared/holding/fundraising/ownership-transfer-dialog/TransferOwnershipReqRes.model';
import {IssueCapitalCallRequestDto} from '../../dashboard/shared/holding/capital-call/models';

@Injectable()
export class GpFundraisingDataService {

  constructor(
    private http: HttpService) {
  }

  generateEndpointPrefix(holdingId: number) {
    return `holdings/${holdingId}/fundraisings/`;
  }

  protected getSearchOptionsToQuerystring(options: SearchOptionsRequest): string {
    return `?filter=${options.filter ? options.filter : ''}&sortOrder=${options.sortOrder ? options.sortOrder : ''}&pageNumber=${options.pageNumber ? options.pageNumber : 0
      }&pageSize=${options.pageSize || 1000}&orderBy=${options.orderBy}`;
  }

  public getList(holdingId: number, options: SearchOptionsRequest): Observable<SearchOptionsResponse<FundraisingTableItemResponse>> {
    const querystring = options ? this.getSearchOptionsToQuerystring(options) : '';
    return this.http.getTyped<SearchOptionsResponse<FundraisingTableItemResponse>>(this.generateEndpointPrefix(holdingId) + querystring).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public create(holdingId: number, model: FundraisingReqRes): Observable<FundraisingReqRes> {
    return this.http.postTyped<FundraisingReqRes>(this.generateEndpointPrefix(holdingId), model).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public update(holdingId: number, fundraisingId: number, model: FundraisingReqRes): Observable<FundraisingReqRes> {
    return this.http.putTyped<FundraisingReqRes>(this.generateEndpointPrefix(holdingId) + fundraisingId, model).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public delete(holdingId: number, fundraisingId: number): Observable<void> {
    return this.http.deleteTyped<void>(this.generateEndpointPrefix(holdingId) + fundraisingId).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public isFundraisingNameExists(holdingId: number, name: string, fundraisingIdToExclude: number = 0) {
    name = name.trim();
    const url = `${this.generateEndpointPrefix(holdingId)}/fundraising-name-exists-check`;
    return this.http.postTyped<boolean>(url, {name, fundraisingIdToExclude}).pipe(shareReplay(1));
  }

  public getById(holdingId: number, fundraisingId: number): Observable<FundraisingReqRes> {
    return this.http.getTyped<FundraisingReqRes>(this.generateEndpointPrefix(holdingId) + fundraisingId).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public updateStatus(holdingId: number, fundraisingId: number, fundraisingStatus: FundraisingStatus): Observable<FundraisingReqRes> {
    return this.http.putTyped<FundraisingReqRes>(this.generateEndpointPrefix(holdingId) + fundraisingId + '/status', fundraisingStatus).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getMultiContributionsTemplate(holdingId: number, contributionsDetails: ContributionDetailsItemReqRes[]): Observable<string> {
    return this.http.postTyped<string>(`${this.generateEndpointPrefix(holdingId)}multitemplate`, contributionsDetails)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public getMultiContributionsImportFromExcelFile(holdingId: number, metaFileLinkId: number): Observable<ContributionsImportResponse> {
    return this.http.getTyped<ContributionsImportResponse>(`${this.generateEndpointPrefix(holdingId)}multitemplate/${metaFileLinkId}`)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public updateOfferingDeck(holdingId: number, fundraisingId: number, offeringDeckId: number, isDefaultDeck: boolean) {
    const url = this.generateEndpointPrefix(holdingId) + fundraisingId + `/offeringDeck?offeringDeckId=${offeringDeckId}&isDefaultDeck=${isDefaultDeck}`;
    return this.http.putTyped(url, offeringDeckId)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public getOfferingDeckEngagementData(holdingId: number, fundraisingId: number): Observable<OfferingDeckEngagementResponse> {
    const url = this.generateEndpointPrefix(holdingId) + fundraisingId + '/offeringDeckEngagement';
    return this.http.getTyped<OfferingDeckEngagementResponse>(url).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public deleteOfferingDeck(holdingId: number, fundraisingId: number, offeringDeckId: number) {
    const url = this.generateEndpointPrefix(holdingId) + fundraisingId + '/offeringDeck/' + offeringDeckId;
    return this.http.deleteTyped<FundraisingReqRes>(url)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public transferOwnership(holdingId: number, model: TransferOwnershipReqRes): Observable<FundraisingReqRes> {
    const url = this.generateEndpointPrefix(holdingId) + '/transfer-ownership';
    return this.http.postTyped<FundraisingReqRes>(url, model).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public exportFundraisings(fileType: "excel" | "csv", holdingId: number = null, fundraisingId: number = null): Observable<string> {

    var queryParams = [];

    queryParams.push(`fileType=${fileType}`);

    if (holdingId) {
      queryParams.push(`holdingId=${holdingId}`);
    }
    if (fundraisingId) {
      queryParams.push(`fundraisingId=${fundraisingId}`);
    }

    const queryParamsStr = queryParams.join('&');

    return this.http.getTyped<string>(`/fundraisings/export` + (queryParams.length ? `?${queryParamsStr}` : '')).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public issueCapitalCall(holdingId: number, fundraisingId: number, issueCCReq: IssueCapitalCallRequestDto): Observable<FundraisingReqRes> {
    const endpoint = `${this.generateEndpointPrefix(holdingId)}${fundraisingId}/issue-capital-call`;
    return this.http.postTyped<FundraisingReqRes>(endpoint, issueCCReq).pipe(
      catchError(err => throwError(err))
    );
  }
}
